import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import {
  useApiClientsOrNull,
  useMediaQuery,
  LoaderMolecule,
  MessageAtom,
  ComposerShellOrganism,
  ConfirmDialogAtom,
  confirmDialogAtom,
  createComposerState,
  serializeForSave,
  lintClosure,
  clearContextViewOverride,
  clearAllOverrides,
  setContextViewOverride,
  applyOverrideToDocument,
  composerEditReducer,
  acceptGhost,
  rejectGhost,
  proposeGhost,
  nextFreeGridPosition,
  useRecentTemplateTouch,
  type ComposerState,
  type ApproachClosure,
  type ClosureLintFinding,
  type TryBarOverrideKind,
  type ContextViewBinding,
  type GhostProposal,
  type ComposablesSearchResult,
  type GridPosition,
} from 'design-system/my-lib';
import type { SelectedNode } from 'design-system/my-lib';
import type { SpaceDocument, SpaceSettings, SpaceWire } from 'types/space';
import type { RootState } from '@center-pwa/store/store';
import './ComposeTargetPage.scss';

const COMPOSER_AGENT_ENDPOINT = process.env.REACT_APP_COMPOSER_AGENT_ENDPOINT;

interface MetaspaceReadResponse {
  metaspace_id: string;
  name: string;
  description?: string;
  canEdit: boolean;
  version: number | null;
  definition: unknown;
  draft?: { version: number; definition: SpaceDocument } | null;
}

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; messageKey: string }
  | { status: 'unsupported' }
  | { status: 'ready'; name: string; canEdit: boolean };

/**
 * `/compose/space/:id` (+ `?scope=approach:<systemName>`) — the composer shell for a Space
 * (metaspace template) target. Loads the caller's editable draft when they hold `edit`
 * (falling back to the published definition), seeds composer-local state
 * (`createComposerState`), and renders `ComposerShellOrganism`. Covers SC2 (canvas-renders-
 * and-selects), SC3 (draft save / publish / stale-draft conflict), and SC7's thin slice
 * (approach-scope closure lint + add-to-approach). Try/Wires modes and the agent thread are
 * later phases of this spec.
 */
const ComposeTargetPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const logAnalyticsEvent = useAnalytics();
  const apiClients = useApiClientsOrNull();
  const touchRecentTemplate = useRecentTemplateTouch(apiClients);
  const [searchParams] = useSearchParams();
  const belowDesktop = useMediaQuery('(max-width: 1023px)');
  // Seeds the composer's anchor sphere with the signed-in user's own sphere (composer-local
  // state from here — `ComposerShellOrganism` never dispatches `setSphereId`/navigates). Absent
  // when the profile hasn't loaded a userSphere yet; the picker flow stays available regardless.
  const userSphereId = useSelector((state: RootState) => state.userProfile?.userSphere?.id);

  const agentFocusOnMount = searchParams.get('agent') === '1';

  const approachSystemName = useMemo(() => {
    const scope = searchParams.get('scope');
    return scope?.startsWith('approach:') ? scope.slice('approach:'.length) : null;
  }, [searchParams]);

  // Deep-link target from a Studio Desk finding row (EAINT-865 gap 14) — `?node=<componentId>`.
  // Resolution into a `SelectedNode` happens inside the composer (`ComposerShellOrganism` /
  // `CompositionHost`), not here, since it needs the same expanded-tree + catalog resolution the
  // canvas/inspector already do internally.
  const initialSelectedNodeId = searchParams.get('node') ?? undefined;

  const [load, setLoad] = useState<LoadState>({ status: 'loading' });
  const [composerState, setComposerState] = useState<ComposerState | null>(null);
  const [selectedNode, setSelectedNode] = useState<SelectedNode | null>(null);
  const [loadedVersion, setLoadedVersion] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [closure, setClosure] = useState<ApproachClosure | null>(null);
  const [approvedOutOfClosure, setApprovedOutOfClosure] = useState<Set<string>>(new Set());
  // The Space's header fields (EAINT-844 Phase F5b) — `metaspace_templates.name`/`.description`,
  // not part of `SpaceDocument`, so they're tracked separately from `composerState` and sent
  // alongside `definition` on every draft save (mirrors `updateMetaspace.ts`'s existing
  // header-mirrors-immediately semantics — no separate dirty-tracking for these two fields).
  const [spaceHeader, setSpaceHeader] = useState<{ name: string; description: string }>({ name: '', description: '' });

  // Nothing below desktop ever renders the composer (the page bails to a desktop-only
  // notice), so don't spend the document read, the closure fetch, or the lint on it.
  const loadTarget = useCallback(async () => {
    if (!id || !apiClients?.schemaClient || belowDesktop) return;

    setLoad({ status: 'loading' });

    try {
      const data = await apiClients.schemaClient.get<MetaspaceReadResponse>(
        `/metaspaces/${encodeURIComponent(id)}?includeDraft=true`
      );

      const rawDocument = data.draft?.definition ?? (data.definition as SpaceDocument | null);
      if (!rawDocument || (rawDocument as SpaceDocument).formatVersion !== 'space/v1') {
        // Legacy `layout[]` metaspaces have no draft/override/ghost machinery to compose
        // against — out of scope until they're converted (EAINT-843 conversion migration).
        setLoad({ status: 'unsupported' });
        return;
      }

      setComposerState(createComposerState(rawDocument as SpaceDocument));
      setLoadedVersion(data.draft?.version ?? data.version ?? null);
      setSpaceHeader({ name: data.name ?? '', description: data.description ?? '' });
      setLoad({ status: 'ready', name: data.name, canEdit: data.canEdit });
      logAnalyticsEvent('composer_opened', undefined, {
        targetKind: 'space',
        scopeKind: approachSystemName ? 'approach' : 'org',
      });
      touchRecentTemplate(id);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      setLoad({ status: 'error', messageKey: status === 404 ? 'composer.page.notFound' : 'composer.page.loadFailed' });
    }
  }, [id, apiClients?.schemaClient, belowDesktop, logAnalyticsEvent, approachSystemName, touchRecentTemplate]);

  useEffect(() => {
    loadTarget();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, apiClients?.schemaClient, belowDesktop]);

  useEffect(() => {
    let cancelled = false;
    if (!approachSystemName || !apiClients?.schemaClient || belowDesktop) {
      setClosure(null);
      return undefined;
    }

    (async () => {
      try {
        const data = await apiClients.schemaClient.get<ApproachClosure>(
          `/approaches/${encodeURIComponent(approachSystemName)}/closure`
        );
        if (!cancelled) setClosure(data);
      } catch {
        if (!cancelled) setClosure(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [approachSystemName, apiClients?.schemaClient, belowDesktop]);

  // Undefined (no approach scope, or the closure hasn't loaded yet) leaves the inspector's
  // context/view pickers unfiltered — same "no scope yet" posture as `lintFindings` below.
  const closureContextSystemNames = useMemo<string[] | undefined>(() => {
    if (!approachSystemName || !closure) return undefined;
    return closure.members.filter((m) => m.itemType === 'context').map((m) => m.systemName);
  }, [approachSystemName, closure]);

  const lintFindings = useMemo<ClosureLintFinding[]>(() => {
    if (!approachSystemName || !closure || !composerState) return [];
    return lintClosure(composerState.draft, closure).filter(
      (finding) => !(finding.itemType === 'composition' && approvedOutOfClosure.has(finding.systemName))
    );
  }, [approachSystemName, closure, composerState, approvedOutOfClosure]);

  const handleBack = useCallback(() => navigate('/studio/space'), [navigate]);

  const handleOpenInPlayground = useCallback(
    (systemName: string) => navigate(`/studio/space?openEntry=${encodeURIComponent(systemName)}`),
    [navigate]
  );

  const handleSave = useCallback(async () => {
    if (!id || !composerState || !apiClients?.schemaClient) return;
    setSaving(true);
    setSaveError(null);
    try {
      const result = await apiClients.schemaClient.patch<{ metaspace: { version: number } }>(
        `/metaspaces/${encodeURIComponent(id)}`,
        {
          definition: serializeForSave(composerState),
          expectedVersion: loadedVersion ?? undefined,
          name: spaceHeader.name,
          description: spaceHeader.description,
        }
      );
      setComposerState((prev) => (prev ? { ...prev, dirty: false } : prev));
      setLoadedVersion(result.metaspace.version);
      logAnalyticsEvent('composer_draft_saved', undefined, {
        targetKind: 'space',
        scopeKind: approachSystemName ? 'approach' : 'org',
      });
      touchRecentTemplate(id);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        confirmDialogAtom({
          header: t('composer.page.staleDraftConflictHeader'),
          message: t('composer.page.staleDraftConflictMessage'),
          acceptLabel: t('composer.page.staleDraftConflictReload'),
          rejectLabel: t('composer.page.staleDraftConflictCancel'),
          accept: () => {
            loadTarget();
          },
          reject: () => {
            /* dialog dismissal — the caller keeps their unsaved edits until they choose to reload */
          },
        });
      } else {
        setSaveError(status === 403 ? t('composer.page.saveForbidden') : t('composer.page.saveFailed'));
      }
    } finally {
      setSaving(false);
    }
  }, [id, composerState, apiClients?.schemaClient, logAnalyticsEvent, t, loadedVersion, loadTarget, approachSystemName, spaceHeader, touchRecentTemplate]);

  const handlePublish = useCallback(async () => {
    if (!id || !apiClients?.schemaClient) return;
    setPublishing(true);
    setPublishError(null);
    try {
      const result = await apiClients.schemaClient.post<{ metaspace: { version: number } }>(
        `/metaspaces/${encodeURIComponent(id)}/publish`
      );
      setLoadedVersion(result.metaspace.version);
      logAnalyticsEvent('composer_published', undefined, {
        targetKind: 'space',
        scopeKind: approachSystemName ? 'approach' : 'org',
      });
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      setPublishError(status === 403 ? t('composer.page.publishForbidden') : t('composer.page.publishFailed'));
    } finally {
      setPublishing(false);
    }
  }, [id, apiClients?.schemaClient, logAnalyticsEvent, t, approachSystemName]);

  const handleAddToApproach = useCallback(
    (finding: ClosureLintFinding) => {
      if (!approachSystemName || !apiClients?.schemaClient) return;
      confirmDialogAtom({
        header: t('composer.page.addToApproachConfirmHeader'),
        message: t('composer.page.addToApproachConfirmMessage'),
        acceptLabel: t('composer.page.addToApproachConfirmAccept'),
        rejectLabel: t('composer.page.addToApproachConfirmReject'),
        accept: async () => {
          try {
            // `ClosureLintFinding` carries no version — the lint only knows WHICH composition
            // is out-of-closure, not which version was placed on the canvas. Omitting
            // `composition.version` binds the latest version.
            await apiClients.schemaClient!.post(`/approaches/${encodeURIComponent(approachSystemName)}/surface-bindings`, {
              when: {},
              then: { kind: 'composition', composition: { systemName: finding.systemName }, hostContext: 'space' },
            });
            setApprovedOutOfClosure((prev) => new Set(prev).add(finding.systemName));
          } catch {
            setPublishError(t('composer.page.addToApproachFailed'));
          }
        },
        reject: () => {
          /* dialog dismissal — the lint finding stays until the caller confirms or removes the ref */
        },
      });
    },
    [approachSystemName, apiClients?.schemaClient, t]
  );
  const agentGetToken = useCallback(
    async () => {
      // The org-bearing session token from the ApiClients tokenProvider - the same source every
      // API call uses. There is no second token source: a bare identity token would carry no
      // organization claim and 401 org-scoped routes when the agent service forwards it.
      const token = await apiClients?.getAuthToken?.();
      if (!token) throw new Error('No session token available for the agent client');
      return token;
  },
    [apiClients]
  );

  const handleProposeGhost = useCallback((ghost: GhostProposal) => {
    setComposerState((prev) => (prev ? proposeGhost(prev, ghost) : prev));
  }, []);

  /**
   * Accepting a ghost always rebases its components into the draft immediately — the thread's
   * proposal card shows "Applied to draft" the moment it's clicked, so the draft must actually
   * match that by the time this returns (no dialog can be left blocking the merge). When the
   * merge introduces an out-of-closure composition ref, this then raises the SAME confirmation
   * dialog `handleAddToApproach` uses for the manual lint-strip chip — declining it just leaves
   * the new ref sitting in the lint strip afterward, where "add to approach" is still available.
   */
  const handleAcceptGhost = useCallback(
    (ghostId: string) => {
      const current = composerState;
      const ghost = current?.ghosts.find((g) => g.id === ghostId);
      setComposerState((prev) => (prev ? acceptGhost(prev, ghostId) : prev));
      if (!current || !ghost) return;

      if (approachSystemName && closure && apiClients?.schemaClient) {
        const draftWithGhost: SpaceDocument = {
          ...current.draft,
          a2ui: { ...current.draft.a2ui, components: [...current.draft.a2ui.components, ...ghost.components] },
        };
        const newFindings = lintClosure(draftWithGhost, closure).filter(
          (finding) => finding.itemType === 'composition' && !approvedOutOfClosure.has(finding.systemName)
        );

        if (newFindings.length > 0) {
          confirmDialogAtom({
            header: t('composer.page.addToApproachConfirmHeader'),
            message: t('composer.page.agentGhostAddToApproachMessage'),
            acceptLabel: t('composer.page.addToApproachConfirmAccept'),
            rejectLabel: t('composer.page.addToApproachConfirmReject'),
            accept: async () => {
              try {
                await Promise.all(
                  newFindings.map((finding) =>
                    apiClients.schemaClient!.post(`/approaches/${encodeURIComponent(approachSystemName)}/surface-bindings`, {
                      when: {},
                      then: { kind: 'composition', composition: { systemName: finding.systemName }, hostContext: 'space' },
                    })
                  )
                );
                setApprovedOutOfClosure((prevSet) => {
                  const next = new Set(prevSet);
                  newFindings.forEach((finding) => next.add(finding.systemName));
                  return next;
                });
              } catch {
                setPublishError(t('composer.page.addToApproachFailed'));
              }
            },
            reject: () => {
              /* dialog dismissal — the ref stays in the lint strip, where "add to approach" is still available */
            },
          });
        }
      }
    },
    [composerState, approachSystemName, closure, apiClients?.schemaClient, approvedOutOfClosure, t]
  );

  const handleRejectGhost = useCallback((ghostId: string) => {
    setComposerState((prev) => (prev ? rejectGhost(prev, ghostId) : prev));
  }, []);

  const handleGhostAccepted = useCallback(
    (componentCount: number) => {
      logAnalyticsEvent('composer_ghost_accepted', undefined, { componentCount });
    },
    [logAnalyticsEvent]
  );

  const handleGhostRejected = useCallback(
    (componentCount: number) => {
      logAnalyticsEvent('composer_ghost_rejected', undefined, { componentCount });
    },
    [logAnalyticsEvent]
  );

  const handleContextConfirmed = useCallback(() => {
    logAnalyticsEvent('composer_context_created', undefined, { scopeKind: approachSystemName ? 'approach' : 'org' });
  }, [logAnalyticsEvent, approachSystemName]);

  const handleContextCancelled = useCallback(() => {
    logAnalyticsEvent('composer_context_cancelled', undefined, { scopeKind: approachSystemName ? 'approach' : 'org' });
  }, [logAnalyticsEvent, approachSystemName]);

  const handleAgentMessageSent = useCallback(() => {
    logAnalyticsEvent('composer_agent_message_sent', undefined, { scopeKind: approachSystemName ? 'approach' : 'org' });
  }, [logAnalyticsEvent, approachSystemName]);

  const handleOverrideApplied = useCallback(
    (overrideKind: TryBarOverrideKind) => {
      logAnalyticsEvent('composer_override_applied', undefined, { overrideKind });
    },
    [logAnalyticsEvent]
  );

  const handleClearOverride = useCallback((componentId: string) => {
    setComposerState((prev) => (prev ? clearContextViewOverride(prev, componentId) : prev));
  }, []);

  const handleClearAllOverrides = useCallback(() => {
    setComposerState((prev) => (prev ? clearAllOverrides(prev) : prev));
  }, []);

  const handleSetOverride = useCallback((componentId: string, binding: ContextViewBinding) => {
    setComposerState((prev) => (prev ? setContextViewOverride(prev, componentId, binding) : prev));
  }, []);

  const handleApplyOverrideToDocument = useCallback((componentId: string) => {
    setComposerState((prev) => (prev ? applyOverrideToDocument(prev, componentId) : prev));
  }, []);

  const handleAddWire = useCallback((wire: SpaceWire) => {
    setComposerState((prev) => (prev ? composerEditReducer(prev, { type: 'addWire', wire }) : prev));
  }, []);

  // Places a rail/agent-search result into the draft at the next free grid row (EAINT-844 Phase
  // F4a). The lint strip picks up an out-of-closure placement for free on the next render — it
  // re-derives from `composerState.draft`, the same as `handleAddToApproach`'s manual chip flow.
  const handleAddFromRail = useCallback((result: ComposablesSearchResult) => {
    setComposerState((prev) => {
      if (!prev) return prev;
      const entry = {
        id: crypto.randomUUID(),
        component: result.systemName,
        gridPosition: nextFreeGridPosition(prev.draft.a2ui.components),
      };
      return composerEditReducer(prev, { type: 'placeComponent', entry });
    });
  }, []);

  const handleSwapSurface = useCallback((component: string) => {
    setComposerState((prev) => (prev ? composerEditReducer(prev, { type: 'replaceSurface', component }) : prev));
  }, []);

  const handleSetGridPosition = useCallback((componentId: string, gridPosition: GridPosition) => {
    setComposerState((prev) => (prev ? composerEditReducer(prev, { type: 'setGridPosition', componentId, gridPosition }) : prev));
  }, []);

  const handleNameChange = useCallback((name: string) => {
    setSpaceHeader((prev) => ({ ...prev, name }));
  }, []);

  const handleDescriptionChange = useCallback((description: string) => {
    setSpaceHeader((prev) => ({ ...prev, description }));
  }, []);

  const handleSettingsChange = useCallback((patch: Partial<SpaceSettings>) => {
    setComposerState((prev) => (prev ? composerEditReducer(prev, { type: 'setSettings', patch }) : prev));
  }, []);

  const handleRemoveWire = useCallback((wireId: string) => {
    setComposerState((prev) => (prev ? composerEditReducer(prev, { type: 'removeWire', wireId }) : prev));
  }, []);

  const agentScope = useMemo(
    () => (approachSystemName ? ({ kind: 'approach' as const, systemName: approachSystemName }) : ({ kind: 'org' as const })),
    [approachSystemName]
  );

  // Informational context for the agent's system prompt only (never used for validation) — a
  // cheap shape summary is enough, no need for a real content hash.
  const agentDraftDigest = useMemo(
    () => (composerState ? `${composerState.draft.a2ui.components.length} components, dirty=${composerState.dirty}` : undefined),
    [composerState]
  );

  if (belowDesktop) {
    return (
      <div className="compose-target-page compose-target-page--desktop-only">
        <MessageAtom severity="info" text={t('composer.page.desktopOnlyTitle')} />
        <p className="compose-target-page__desktop-only-description">{t('composer.page.desktopOnlyDescription')}</p>
      </div>
    );
  }

  if (load.status === 'loading' || !composerState) {
    return (
      <div className="compose-target-page compose-target-page--loading">
        <LoaderMolecule variant="inline" caption={t('composer.page.loading')} />
      </div>
    );
  }

  if (load.status === 'unsupported') {
    return (
      <div className="compose-target-page compose-target-page--message">
        <MessageAtom severity="warn" text={t('composer.page.unsupportedFormat')} />
      </div>
    );
  }

  if (load.status === 'error') {
    return (
      <div className="compose-target-page compose-target-page--message">
        <MessageAtom severity="error" text={t(load.messageKey)} />
      </div>
    );
  }

  return (
    <div className="compose-target-page">
      <ConfirmDialogAtom />
      {saveError && (
        <div className="compose-target-page__save-error">
          <MessageAtom severity="error" text={saveError} />
        </div>
      )}
      {publishError && (
        <div className="compose-target-page__save-error">
          <MessageAtom severity="error" text={publishError} />
        </div>
      )}
      {/* `perspectives` is left unset (TryBar's viewer-as switcher renders disabled): the anchor
          sphere is seeded from the user's own sphere (`sphereId` below) but never fetched here as
          a full `Sphere`, so there is no sphere to read `.perspectives` off of yet. */}
      <ComposerShellOrganism
        state={composerState}
        apiClient={apiClients?.schemaClient}
        searchApiClient={apiClients?.searchClient}
        onOpenComposableInPlayground={handleOpenInPlayground}
        onAddFromRail={load.canEdit ? handleAddFromRail : undefined}
        onSwapSurface={load.canEdit ? handleSwapSurface : undefined}
        sphereId={userSphereId}
        draftVersion={loadedVersion}
        title={load.name}
        selectedNode={selectedNode}
        onSelect={setSelectedNode}
        initialSelectedNodeId={initialSelectedNodeId}
        onSave={load.canEdit ? handleSave : undefined}
        saving={saving}
        onBack={handleBack}
        onPublish={load.canEdit ? handlePublish : undefined}
        publishing={publishing}
        lintFindings={lintFindings}
        onAddToApproach={approachSystemName ? handleAddToApproach : undefined}
        closureContextSystemNames={closureContextSystemNames}
        onOverrideApplied={handleOverrideApplied}
        onClearOverride={load.canEdit ? handleClearOverride : undefined}
        onClearAllOverrides={load.canEdit ? handleClearAllOverrides : undefined}
        onSetOverride={load.canEdit ? handleSetOverride : undefined}
        onApplyOverrideToDocument={load.canEdit ? handleApplyOverrideToDocument : undefined}
        onAddWire={load.canEdit ? handleAddWire : undefined}
        onRemoveWire={load.canEdit ? handleRemoveWire : undefined}
        onSetGridPosition={load.canEdit ? handleSetGridPosition : undefined}
        spaceName={spaceHeader.name}
        spaceDescription={spaceHeader.description}
        onNameChange={load.canEdit ? handleNameChange : undefined}
        onDescriptionChange={load.canEdit ? handleDescriptionChange : undefined}
        onSettingsChange={load.canEdit ? handleSettingsChange : undefined}
        agentEndpoint={COMPOSER_AGENT_ENDPOINT}
        agentGetToken={agentGetToken}
        agentTarget={id ? { kind: 'space', id } : undefined}
        agentScope={agentScope}
        agentDraftDigest={agentDraftDigest}
        onProposeGhost={handleProposeGhost}
        onAcceptGhost={handleAcceptGhost}
        onRejectGhost={handleRejectGhost}
        onAgentMessageSent={handleAgentMessageSent}
        onGhostAccepted={handleGhostAccepted}
        onGhostRejected={handleGhostRejected}
        onContextConfirmed={handleContextConfirmed}
        onContextCancelled={handleContextCancelled}
        agentFocusOnMount={agentFocusOnMount}
        onOpenInPlayground={handleOpenInPlayground}
      />
    </div>
  );
};

export default ComposeTargetPage;
